import { apiClient } from './apiClient';

export interface MiradorDomainEntry {
  table_name: string;
  enabled: boolean;
}

export interface MiradorClientItem {
  client_id: string;
  slug: string;
  name: string;
  enabled: boolean;
  domains: Record<string, MiradorDomainEntry>;
}

export interface HealthResult {
  ok: boolean;
  latency_ms: number | null;
  error: string | null;
  last_checked_at: string;
}

export interface OutboxStats {
  pending: number;
  processing: number;
  processed_24h: number;
  failed_24h: number;
}

export interface OutboxEntry {
  id: string;
  client_id: string;
  client_slug: string | null;
  event_type: string;
  document_id: string;
  extraction_id: string;
  status: string;
  attempt_count: number;
  last_error: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface MiradorConfigPayload {
  database_url: string;
  enabled: boolean;
  domains: Record<string, MiradorDomainEntry>;
}

export interface MiradorConfigRead {
  configured: boolean;
  enabled: boolean;
  domains: Record<string, MiradorDomainEntry>;
  database_url_masked: string | null;
}

export interface SchemaCoverage {
  client_slug: string;
  domain: string;
  schema_version: string;
  covered: string[];
  missing: string[];
  extra_dokimo: string[];
}

const BASE = '/admin/mirador';

const qs = (params: Record<string, string | number | undefined>): string => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
};

export const adminMiradorService = {
  async listClients(): Promise<MiradorClientItem[]> {
    const r = await apiClient.get<MiradorClientItem[]>(`${BASE}/clients`);
    return r.data;
  },

  async health(clientId: string): Promise<HealthResult> {
    const r = await apiClient.get<HealthResult>(`${BASE}/health${qs({ client_id: clientId })}`);
    return r.data;
  },

  async outboxStats(clientId?: string): Promise<OutboxStats> {
    const r = await apiClient.get<OutboxStats>(`${BASE}/outbox/stats${qs({ client_id: clientId })}`);
    return r.data;
  },

  async listOutbox(opts: {
    status?: string;
    client_id?: string;
    limit?: number;
  } = {}): Promise<OutboxEntry[]> {
    const r = await apiClient.get<OutboxEntry[]>(`${BASE}/outbox${qs(opts)}`);
    return r.data;
  },

  async retryOutbox(id: string): Promise<{ ok: boolean }> {
    const r = await apiClient.post<{ ok: boolean }>(`${BASE}/outbox/${id}/retry`);
    return r.data;
  },

  async getMiradorConfig(clientId: string): Promise<MiradorConfigRead> {
    const r = await apiClient.get<MiradorConfigRead>(`/admin/clients/${clientId}/mirador-config`);
    return r.data;
  },

  async putMiradorConfig(clientId: string, payload: MiradorConfigPayload): Promise<{ ok: boolean }> {
    const r = await apiClient.put<{ ok: boolean }>(`/admin/clients/${clientId}/mirador-config`, payload);
    return r.data;
  },

  async deleteMiradorConfig(clientId: string): Promise<void> {
    await apiClient.delete(`/admin/clients/${clientId}/mirador-config`);
  },

  async testMirador(clientId: string, databaseUrl?: string): Promise<HealthResult> {
    const r = await apiClient.post<HealthResult>(
      `/admin/clients/${clientId}/test-mirador`,
      databaseUrl ? { database_url: databaseUrl } : null,
    );
    return r.data;
  },

  async schemaCoverage(clientId: string, domain: string): Promise<SchemaCoverage> {
    const r = await apiClient.get<SchemaCoverage>(
      `${BASE}/schema-coverage${qs({ client_id: clientId, domain })}`,
    );
    return r.data;
  },
};
